import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IVideo, VideoApproveRequest } from 'src/app/interfaces/video.interface';
import { environment } from 'src/environments/environment';
import { IPaging } from "../interfaces/paging.interface";
import { map } from "rxjs/operators";
import { IUser } from "../interfaces/user.interface";
import { IUploadRequest, UploadService } from './upload.service';
import { ParamsUtil } from '../helpers/params-util';
import { NO_TEAMS_TOKEN } from '../interceptors/teams.interceptor';
import { NO_ERROR_TOKEN } from '../interceptors/error.interceptor';
import { IVideoStatistics } from '../interfaces/video-statistics.interface';
import { IUploadConfirmRequest } from "../pages/upload/interfaces/upload-form.interface";

@Injectable({
  providedIn: 'root'
})
export class VideoService extends UploadService<IUploadConfirmRequest> {
  requestUploadUrl = `${environment.apiUrl}/Videos/Upload/Request`;
  confirmUploadUrl = `${environment.apiUrl}/Videos/Upload/Confirm`;

  private apiUrl = environment.apiUrl + '/videos';

  public getVideos(data?: any, noTeams = false): Observable<IPaging<IVideo>> {
    const params = ParamsUtil.prepareParams(data);

    const context = new HttpContext();
    context.set(NO_TEAMS_TOKEN, noTeams);

    return this.http.get<{ result: IPaging<IVideo>, users: IUser[] }>(this.apiUrl, {
      params,
      context
    })
      .pipe(
        map((res) => {
          const videos = res.result.items.map((v: IVideo) => {
            v.user = res.users.find(u => v.user == u.sid);
            return v;
          });
          res.result.items = videos;
          return res.result;
        })
      );
  }

  /**
   * De andere camera's van studioclips, voor de camerawissel in de
   * afspeellijst (11-10-2026): per clip alle clips van hetzelfde event, de
   * clip zelf ook. Arch `GET /Videos/ClipAngles`, hoogstens 200 clips per
   * vraag. Stil: lukt het niet, dan zijn er gewoon geen bolletjes.
   */
  public getClipHoeken(clips: readonly string[]): Observable<IVideo[]> {
    return this.http.get<{ items?: IVideo[] }>(`${this.apiUrl}/ClipAngles`, {
      params: { clips: clips.join(',') },
      context: new HttpContext().set(NO_TEAMS_TOKEN, true).set(NO_ERROR_TOKEN, true),
    }).pipe(map((res) => res?.items ?? []));
  }

  public getVideoById(id: string): Observable<IVideo> {
    return this.http.get<{ video: IVideo, user: IUser }>(`${this.apiUrl}/${id}`).pipe(
      map(res => {
        res.video.user = res.user;
        return res.video;
      })
    );
  }

  public approveVideo(id: string, data: VideoApproveRequest): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}/approve`, data);
  }

  public updateVideo(id: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiUrl}/${id}`, data);
  }

  public deleteVideo(id: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`);
  }

  public incrementView(id: string): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}/view`, {});
  }

  public getStatisticsByClub(clubSid: string): Observable<IVideoStatistics> {
    return this.http.get<{ statistics: IVideoStatistics[] }>(`${this.apiUrl}/statistics`, {params: {clubs: clubSid}})
      .pipe(map(response => {
        if (response.statistics.length) {
          return response.statistics[0];
        }

        return null;
      }));
  }

  public setWatchedTimeForVideo(id: string, time: number): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}/watch`, {secondsWatched: time});
  }

  // Tracking-planning (opt-in): plan een opname in bij de producer-box. `ts` is
  // de box-opname-timestamp; de box pollt zijn openstaande verzoeken op. Gaat
  // naar de gateway-route /api/tracker/requests (club-scoped). Zie
  // TRACKING_REQUESTS_PLAN.md over de video->ts-koppeling.
  public requestTracking(ts: string, matchRef?: string): Observable<any> {
    const context = new HttpContext();
    context.set(NO_TEAMS_TOKEN, true);
    return this.http.post(
      `${environment.apiUrl}/api/tracker/requests`,
      { ts, matchRef },
      { context },
    );
  }

  public getCutoutNames(): Observable<string[]> {
    return this.http.get<{
      cutoutNames: string[]
    }>(`${this.apiUrl}/CutoutNames`).pipe(map(response => response.cutoutNames));
  }

  protected prepareUploadData(partResponse: any, request: IUploadRequest, data: IUploadConfirmRequest): any {
    return {
      upload: {parts: partResponse, key: request.key},
      video: data
    };
  }

}
